import * as CookieConsent from "vanilla-cookieconsent";

function updateGTMConsent(analyticsGranted, marketingGranted) {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: analyticsGranted ? "granted" : "denied",
      ad_storage: marketingGranted ? "granted" : "denied",
      ad_user_data: marketingGranted ? "granted" : "denied",
      ad_personalization: marketingGranted ? "granted" : "denied",
    });
  }
}

function injectClarity() {
  const clarityId = import.meta.env.VITE_CLARITY_PROJECT_ID;
  if (!clarityId || window._gnClarityLoaded) return;
  window._gnClarityLoaded = true;

  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", clarityId);

  // Signal consent to Clarity after script injection
  window.clarity("consent", true);
}

function handleAnalyticsConsent(granted) {
  if (granted) {
    window.dispatchEvent(new CustomEvent("gn:analyticsConsent"));
    injectClarity();
  } else {
    // Opt Mixpanel out if it was already initialised
    try {
      const mp = window.mixpanel;
      if (mp && typeof mp.opt_out_tracking === "function") {
        mp.opt_out_tracking();
      }
    } catch (_) {}
  }
}

function applyConsent(cookie) {
  const analytics = CookieConsent.acceptedCategory("analytics");
  const marketing = CookieConsent.acceptedCategory("marketing");
  updateGTMConsent(analytics, marketing);
  handleAnalyticsConsent(analytics);
}

export function hasAnalyticsConsent() {
  return CookieConsent.acceptedCategory("analytics");
}

export function openCookiePreferences() {
  CookieConsent.showPreferences();
}

export function initCookieConsent() {
  CookieConsent.run({
    cookie: {
      name: "gn_cookie_consent",
      expiresAfterDays: 365,
      sameSite: "Lax",
    },

    guiOptions: {
      consentModal: {
        layout: "bar",
        position: "bottom",
        equalWeightButtons: false,
        flipButtons: false,
      },
      preferencesModal: {
        layout: "box",
        position: "right",
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [
            { name: /^_ga/ },
            { name: "_gid" },
            { name: /^mp_/ },
            { name: "_clck" },
            { name: "_clsk" },
            { name: "MUID" },
          ],
        },
      },
      marketing: {
        enabled: false,
        readOnly: false,
        autoClear: {
          cookies: [
            { name: /^_gcl/ },
            { name: /^_fbp/ },
            { name: /^_fbc/ },
          ],
        },
      },
    },

    language: {
      default: "en",
      translations: {
        en: {
          consentModal: {
            title: "We use cookies",
            description:
              "We use cookies to personalise your experience, analyse site traffic, and serve relevant content. You can choose which cookies to allow below.",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            showPreferencesBtn: "Manage preferences",
            footer:
              '<a href="https://www.gurunow.ai/privacy-policy" target="_blank">Privacy Policy</a> · <a href="https://gurunow.ai/terms" target="_blank">Terms</a>',
          },
          preferencesModal: {
            title: "Cookie preferences",
            acceptAllBtn: "Accept all",
            acceptNecessaryBtn: "Reject all",
            savePreferencesBtn: "Save preferences",
            closeIconLabel: "Close",
            serviceCounterLabel: "Service|Services",
            sections: [
              {
                title: "Cookie usage",
                description:
                  "We use different types of cookies to improve your experience on GuruNow. You can choose which categories to allow. Necessary cookies are always active as they are required for the site to function.",
              },
              {
                title: "Strictly necessary",
                description:
                  "These cookies are required for the website to function and cannot be switched off. They are used for authentication, security, and basic site functionality.",
                linkedCategory: "necessary",
                cookieTable: {
                  headers: {
                    name: "Cookie",
                    domain: "Domain",
                    desc: "Description",
                  },
                  body: [
                    {
                      name: "token",
                      domain: "gurunow.ai",
                      desc: "Stores your authentication session",
                    },
                    {
                      name: "GURUNOW_REACT_QUERY_CACHE",
                      domain: "gurunow.ai",
                      desc: "Caches application data for performance",
                    },
                  ],
                },
              },
              {
                title: "Analytics",
                description:
                  "These cookies help us understand how you use GuruNow so we can improve the product. Data collected is anonymous or pseudonymous.",
                linkedCategory: "analytics",
                cookieTable: {
                  headers: {
                    name: "Cookie",
                    domain: "Domain",
                    desc: "Description",
                  },
                  body: [
                    {
                      name: "mp_*",
                      domain: "gurunow.ai",
                      desc: "Mixpanel — product analytics and user behaviour",
                    },
                    {
                      name: "_ga, _gid",
                      domain: "google.com",
                      desc: "Google Analytics (via GTM) — site traffic analysis",
                    },
                    {
                      name: "_clck, _clsk",
                      domain: "clarity.ms",
                      desc: "Microsoft Clarity — session recordings and heatmaps",
                    },
                  ],
                },
              },
              {
                title: "Marketing",
                description:
                  "These cookies are used to show you relevant advertising and track the effectiveness of our marketing campaigns.",
                linkedCategory: "marketing",
                cookieTable: {
                  headers: {
                    name: "Cookie",
                    domain: "Domain",
                    desc: "Description",
                  },
                  body: [
                    {
                      name: "_gcl_*",
                      domain: "google.com",
                      desc: "Google Ads — conversion tracking and remarketing",
                    },
                    {
                      name: "_fbp, _fbc",
                      domain: "facebook.com",
                      desc: "Meta Pixel — ad targeting and conversion tracking",
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    },

    onConsent({ cookie }) {
      applyConsent(cookie);
    },

    onChange({ cookie }) {
      applyConsent(cookie);
    },
  });
}
